import { useEffect, useState } from 'react';
import {
  Box,
  Dialog,
  DialogContent,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Tab,
  Tabs,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { Transition } from '../../components/Transition';
import { ServiceProduct } from '../../client/services';
import DeliverySlotsCalendar from './DeliverySlotsCalendar';
import EventServiceCapacityCalendar, {
  EventServiceProductOption,
} from './EventServiceCapacityCalendar';

interface Props {
  open: boolean;
  onClose: () => void;
  refreshKey?: number;
  onEditSlotsClick?: () => void;
}

const panelSx = (visible: boolean) => ({
  visibility: visible ? 'visible' : 'hidden',
  opacity: visible ? 1 : 0,
  pointerEvents: visible ? 'auto' : 'none',
  position: visible ? 'relative' : 'absolute',
  top: 0,
  left: 0,
  right: 0,
  width: '100%',
  transition: 'opacity 0.18s ease',
});

const DeliverySlotsCalendarDialog = ({
  open,
  onClose,
  refreshKey,
  onEditSlotsClick,
}: Props) => {
  const [tab, setTab] = useState(0);
  const [products, setProducts] = useState<EventServiceProductOption[]>([]);
  const [productId, setProductId] = useState('');
  const [productsLoading, setProductsLoading] = useState(false);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    const loadProducts = async () => {
      setProductsLoading(true);
      try {
        const result: any = await ServiceProduct.find({
          query: { $limit: 1000, $skip: 0 },
        });
        const rows: EventServiceProductOption[] = (
          result?.data ||
          result ||
          []
        ).map((row: any) => ({
          id: String(row.id),
          labelEn: String(row.labelEn || 'Untitled'),
          dailyCapacity:
            row.dailyCapacity == null ? null : Number(row.dailyCapacity),
        }));
        if (cancelled) return;
        setProducts(rows);
        setProductId((prev) => prev || rows[0]?.id || '');
      } catch (_err) {
        if (!cancelled) setProducts([]);
      } finally {
        if (!cancelled) setProductsLoading(false);
      }
    };
    loadProducts();
    return () => {
      cancelled = true;
    };
  }, [open]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth='lg'
      TransitionComponent={Transition}
      keepMounted
      sx={{
        '& .MuiDialog-paper': {
          m: { xs: 1.5, sm: 2 },
          width: {
            xs: 'calc(100% - 24px)',
            sm: 'min(1040px, calc(100% - 48px))',
          },
          maxWidth: '1040px !important',
          maxHeight: { xs: 'calc(100% - 24px)', sm: '86vh' },
          display: 'flex',
          flexDirection: 'column',
        },
      }}
    >
      <DialogContent
        sx={{
          position: 'relative',
          px: { xs: 1.5, sm: 2.25 },
          pt: { xs: 1.75, sm: 2 },
          pb: 2,
          overflowY: 'auto',
          minHeight: 0,
        }}
      >
        <IconButton
          onClick={onClose}
          sx={{ position: 'absolute', right: 8, top: 8, zIndex: 2 }}
          aria-label='Close calendar'
        >
          <CloseIcon />
        </IconButton>

        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 2,
            borderBottom: 1,
            borderColor: 'divider',
            mb: 1.5,
            pr: 5,
            flexWrap: 'wrap',
          }}
        >
          <Tabs
            value={tab}
            onChange={(_e, next) => setTab(next)}
            textColor='primary'
            indicatorColor='primary'
            sx={{ minHeight: 44 }}
          >
            <Tab label='Delivery Slots' />
            <Tab label='Event Service Capacity' />
          </Tabs>

          {tab === 1 ? (
            <FormControl
              size='small'
              sx={{
                minWidth: { xs: '100%', sm: 240 },
                maxWidth: 320,
                mb: 0.75,
              }}
            >
              <InputLabel id='event-service-capacity-product-top'>
                Event service
              </InputLabel>
              <Select
                labelId='event-service-capacity-product-top'
                label='Event service'
                value={productId}
                disabled={productsLoading || !products.length}
                onChange={(e) => setProductId(String(e.target.value))}
              >
                {!products.length ? (
                  <MenuItem value=''>
                    <em>No event services found</em>
                  </MenuItem>
                ) : (
                  products.map((p) => (
                    <MenuItem key={p.id} value={p.id}>
                      {p.labelEn}
                      {p.dailyCapacity == null
                        ? ' (unlimited)'
                        : ` (cap ${p.dailyCapacity})`}
                    </MenuItem>
                  ))
                )}
              </Select>
            </FormControl>
          ) : null}
        </Box>

        <Box sx={{ position: 'relative', minHeight: { xs: 420, sm: 520 } }}>
          <Box sx={panelSx(tab === 0)}>
            <DeliverySlotsCalendar
              embedded
              headerTitle='Capacity Calendar'
              refreshKey={refreshKey}
              onEditSlotsClick={onEditSlotsClick}
            />
          </Box>
          <Box sx={panelSx(tab === 1)}>
            <EventServiceCapacityCalendar productId={productId} />
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default DeliverySlotsCalendarDialog;
